<template>
  <v-dialog v-model="show" max-width="500px" persistent>
    <v-card :class="classContents">
      <v-card-title>
        <h3>{{ collectionCycle.stackType }} {{ collectionCycle.systemId }}'s {{ collectionCycle.name }}</h3>
      </v-card-title>
      <v-form ref="form">
        <v-row class="mx-3 mt-3">
          <v-col cols="12" class="pr-5">
            <v-card flat class="dialog-form" :class="classContents">
              <div class="d-flex flex-column">
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ this.$t('cycle.collectYn') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-checkbox v-model="collectionCycle.collect" :disabled="collectionCycle.collect === undefined" style="margin-top: 0px" dense></v-checkbox>
                  </div>
                </div>
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ this.$t('cycle.defaultCycle') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-text-field :value="mis2Msms(collectionCycle.defaultCycle)" readonly outlined dense></v-text-field>
                  </div>
                </div>
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ this.$t('cycle.cycle') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-btn-toggle v-model="collectionCycle.cycle" dense class="collect-cycle">
                      <v-btn v-for="c in collectionCycle.cycles" :key="c" :value="c" style="height: 30px; margin-bottom: 20px">
                        {{ mis2Msms(c) }}
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </div>
                <div class="my-1">
                  <div class="float-left column-name">
                    <span class="d-flex align-center">{{ this.$t('cycle.avoidanceTime') }}</span>
                  </div>
                  <div class="float-left column-contents">
                    <v-range-slider v-model="beginSleepTimeRange" :min="rangeMin" :max="rangeMax" step="6" hide-details>
                      <template v-slot:prepend>
                        <span>{{ convertNumber2TimeString(beginSleepTimeRange[0]) }}</span>
                      </template>
                      <template v-slot:append>
                        <span>{{ convertNumber2TimeString(beginSleepTimeRange[1]) }}</span>
                      </template>
                    </v-range-slider>
                    <v-range-slider v-model="endSleepTimeRange" :min="rangeMin" :max="rangeMax" step="6" hide-details>
                      <template v-slot:prepend>
                        <span>{{ convertNumber2TimeString(endSleepTimeRange[0]) }}</span>
                      </template>
                      <template v-slot:append>
                        <span>{{ convertNumber2TimeString(endSleepTimeRange[1]) }}</span>
                      </template>
                    </v-range-slider>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn @click="close" small>{{ this.$t('common.cancel') }}</v-btn>
        <v-btn @click="reset" small>{{ this.$t('common.reset') }}</v-btn>
        <v-btn @click="save" :loading="loading" small>{{ this.$t('common.save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { api } from '@/api/common/instances';
import { mis2Msms } from '@/utils/formatter';

const RANGE_MIN = 0;
const RANGE_MAX = 288;

export default {
  props: {
    value: Boolean,
    formData: Object,
  },
  data() {
    return {
      collectionCycle: {
        code: '',
        stackType: '',
        systemId: '',
        name: '',
        collecting: false,
        defaultCycle: 0,
        cycle: 0,
        cycles: [],
        sleepTimes: [],
      },
      loading: false,
      rangeMin: RANGE_MIN,
      rangeMax: RANGE_MAX,
      beginSleepTimeRange: [RANGE_MIN, RANGE_MIN],
      endSleepTimeRange: [RANGE_MIN, RANGE_MIN],
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    classContents() {
      return {
        'contents-background-dark': this.$vuetify.theme.isDark,
      };
    },
  },
  watch: {
    formData(value) {
      this.collectionCycle = Object.assign({}, value);
      const c = this.convertTimeString2Number;
      if (this.collectionCycle.sleepTimes.length > 0) {
        const times = this.collectionCycle.sleepTimes[0].match(/([0-9][0-9])/g);
        this.beginSleepTimeRange = [c(times[0], 'h') + c(times[1]), c(times[2], 'h') + c(times[3])];
      } else {
        this.beginSleepTimeRange = [RANGE_MIN, RANGE_MIN];
      }

      if (this.collectionCycle.sleepTimes.length > 1) {
        const times = this.collectionCycle.sleepTimes[1].match(/([0-9][0-9])/g);
        this.endSleepTimeRange = [c(times[0], 'h') + c(times[1]), c(times[2], 'h') + c(times[3])];
      } else {
        this.endSleepTimeRange = [RANGE_MIN, RANGE_MIN];
      }
    },
  },
  methods: {
    async save() {
      this.startLoading();
      const c = this.convertNumber2TimeString;
      this.collectionCycle.sleepTimes = [];
      if (this.beginSleepTimeRange[0] !== RANGE_MIN || this.beginSleepTimeRange[1] !== RANGE_MIN) {
        this.collectionCycle.sleepTimes[0] = c(this.beginSleepTimeRange[0]) + '~' + c(this.beginSleepTimeRange[1]);
      }
      if (this.endSleepTimeRange[0] !== RANGE_MIN || this.endSleepTimeRange[1] !== RANGE_MIN) {
        this.collectionCycle.sleepTimes[1] = c(this.endSleepTimeRange[0]) + '~' + c(this.endSleepTimeRange[1]);
      }

      let url = `/preference/monitors/${this.collectionCycle.stackType}`;
      if (this.collectionCycle.systemId) {
        url += '/' + this.collectionCycle.systemId;
      }
      const response = await api.put(url, this.collectionCycle);
      const { status } = response;
      if (status === 200) {
        this.$emit('change', Object.assign({}, this.collectionCycle));
        this.close();
      }
      this.finishLoading();
    },
    reset() {
      if (this.collectionCycle.systemId) {
        this.collectionCycle.collect = true;
      }
      this.collectionCycle.cycle = this.collectionCycle.defaultCycle;
      this.beginSleepTimeRange = [RANGE_MIN, RANGE_MIN];
      this.endSleepTimeRange = [RANGE_MIN, RANGE_MIN];
    },
    close() {
      this.show = false;
    },
    convertNumber2TimeString(val) {
      const hour = Math.floor(val / 12);
      const minute = Math.floor((val % 12) * 5);
      return hour.toString().padStart(2, '0') + ':' + minute.toString().padStart(2, '0');
    },
    convertTimeString2Number(val, type) {
      const t = parseInt(val);
      return type === 'h' ? t * 12 : t / 5;
    },
    startLoading() {
      this.loading = true;
    },
    finishLoading() {
      this.loading = false;
    },
    mis2Msms,
  },
};
</script>

<style scoped>
.collect-cycle {
  height: 31px;
}
</style>
